<template>
  <div class="recurso-analise">
    <div class="page-header">
      <div>
        <h1 class="page-title">
          <i class="fas fa-search"></i>
          Análise de Recurso
        </h1>
        <p class="page-subtitle" v-if="recurso">Recurso #{{ recurso.id }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="voltar">
          <i class="fas fa-arrow-left"></i>
          Voltar
        </button>
      </div>
    </div>

    <div v-if="loading" class="state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Carregando recurso...</p>
    </div>

    <div v-else-if="!recurso" class="state">
      <i class="fas fa-exclamation-triangle"></i>
      <p>Recurso não encontrado.</p>
    </div>

    <div v-else class="content">
      <div class="card">
        <h3 class="card-title">
          <i class="fas fa-info-circle"></i>
          Status Atual
        </h3>
        <div class="grid">
          <div class="item">
            <label>Status</label>
            <span :class="getStatusClass(recurso.status)">{{ getStatusText(recurso.status) }}</span>
          </div>
          <div class="item">
            <label>Data da Interposição</label>
            <span>{{ formatDate(recurso.created_at) }}</span>
          </div>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">
          <i class="fas fa-user"></i>
          Dados do Candidato
        </h3>
        <div class="grid">
          <div class="item">
            <label>Nome</label>
            <span>{{ recurso.inscricao?.cadastro?.nome || '-' }}</span>
          </div>
          <div class="item">
            <label>CPF</label>
            <span>{{ formatCPF(recurso.inscricao?.cadastro?.cpf) }}</span>
          </div>
          <div class="item">
            <label>E-mail</label>
            <span>{{ recurso.inscricao?.cadastro?.email || '-' }}</span>
          </div>
          <div class="item">
            <label>Telefone</label>
            <span>{{ recurso.inscricao?.cadastro?.telefone_celular || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">
          <i class="fas fa-file-alt"></i>
          Recurso Interposto
        </h3>
        <div class="grid">
          <div class="item">
            <label>Número da Questão</label>
            <span>{{ recurso.numero_questao || '-' }}</span>
          </div>
          <div class="item">
            <label>Prazo de Recurso</label>
            <span>{{ recurso.prazo_recurso?.nome || '-' }}</span>
          </div>
          <div class="item full">
            <label>Motivo/Argumentação</label>
            <span class="pre">{{ recurso.motivo || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">
          <i class="fas fa-gavel"></i>
          {{ podeEditarResposta && !editando ? 'Resposta Enviada' : 'Resposta do Administrador' }}
        </h3>

        <div v-if="!mostrarFormulario">
          <div class="grid">
            <div class="item full">
              <label>Fundamentação</label>
              <span class="pre">{{ recurso.observacao_admin || 'Sem fundamentação registrada' }}</span>
            </div>
          </div>
          <div class="actions">
            <button v-if="podeEditarResposta" class="btn btn-warning" @click="iniciarEdicao">
              <i class="fas fa-pen"></i>
              Editar Resposta
            </button>
          </div>
        </div>

        <div v-else>
          <div class="form">
            <div class="form-group">
              <label>Decisão *</label>
              <select v-model="form.status" class="form-control">
                <option value="">Selecione</option>
                <option value="D">Deferir (Aprovar)</option>
                <option value="I">Indeferir (Negar)</option>
              </select>
            </div>
            <div class="form-group">
              <label>Observação/Fundamentação</label>
              <textarea
                v-model="form.observacao_admin"
                class="form-control"
                rows="5"
                placeholder="Fundamentação da decisão (opcional)"
              ></textarea>
            </div>
          </div>

          <div class="actions">
            <button class="btn btn-secondary" @click="cancelarEdicao" :disabled="saving">
              Cancelar
            </button>
            <button class="btn btn-primary" @click="salvar" :disabled="!form.status || saving">
              <i v-if="saving" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-save"></i>
              {{ saving ? 'Salvando...' : (editando ? 'Salvar Alterações' : 'Registrar Resposta') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'RecursoAnalise',
  data() {
    return {
      loading: false,
      saving: false,
      recurso: null,
      editando: false,
      form: {
        status: '',
        observacao_admin: ''
      }
    }
  },
  computed: {
    podeEditarResposta() {
      return !!this.recurso && this.recurso.status !== 'P'
    },
    mostrarFormulario() {
      return !!this.recurso && (this.recurso.status === 'P' || this.editando)
    }
  },
  mounted() {
    this.carregar()
  },
  methods: {
    voltar() {
      this.$router.push({ name: 'admin-recursos' })
    },
    async carregar() {
      this.loading = true
      try {
        const id = this.$route.params.id
        const response = await axios.get(`/ps/recurso/admin/${id}`)
        this.recurso = response.data

        // se já está pendente, já abre o formulário para responder
        if (this.recurso && this.recurso.status === 'P') {
          this.editando = true
          this.form = { status: '', observacao_admin: '' }
        }
      } catch (error) {
        console.error('Erro ao carregar recurso:', error)
        this.$sweet && this.$sweet.showError && this.$sweet.showError('Erro ao carregar recurso')
        this.recurso = null
      } finally {
        this.loading = false
      }
    },
    iniciarEdicao() {
      if (!this.recurso) return
      this.editando = true
      this.form = {
        status: this.recurso.status || '',
        observacao_admin: this.recurso.observacao_admin || ''
      }
    },
    cancelarEdicao() {
      this.editando = false
      this.form = { status: '', observacao_admin: '' }
    },
    async salvar() {
      if (!this.form.status) {
        this.$sweet && this.$sweet.showWarning && this.$sweet.showWarning('Selecione uma decisão')
        return
      }

      const titulo = this.recurso && this.recurso.status !== 'P' ? 'Confirmar Edição' : 'Confirmar Decisão'
      const acao = this.form.status === 'D' ? 'deferir' : 'indeferir'
      const descricao = this.recurso && this.recurso.status !== 'P'
        ? `Tem certeza que deseja <b>alterar</b> a decisão deste recurso para <b>${acao}</b>?`
        : `Tem certeza que deseja ${acao} este recurso?`

      const { value } = await this.$sweet.showConfirm(titulo, descricao)
      if (!value) return

      this.saving = true
      try {
        await axios.put(`/ps/recurso/resposta/${this.recurso.id}`, this.form)

        this.$sweet.showSuccess(this.recurso.status !== 'P' ? 'Resposta atualizada com sucesso!' : 'Resposta registrada com sucesso!')

        this.recurso = {
          ...this.recurso,
          status: this.form.status,
          observacao_admin: this.form.observacao_admin
        }
        this.editando = false
      } catch (error) {
        console.error('Erro ao salvar resposta:', error)
        this.$sweet && this.$sweet.showError && this.$sweet.showError('Erro ao salvar resposta')
      } finally {
        this.saving = false
      }
    },
    formatCPF(cpf) {
      if (!cpf) return '-'
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },
    formatDate(date) {
      if (!date) return '-'
      try {
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = date.split('-')
          return `${day}/${month}/${year}`
        }
        if (typeof date === 'string') {
          let dateString = date.replace('Z', '')
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ')
            const [year, month, day] = datePart.split('-')
            return `${day}/${month}/${year}`
          }
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '')
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T')
              const [year, month, day] = datePart.split('-')
              return `${day}/${month}/${year}`
            }
          }
        }
        const dateObj = new Date(date)
        if (isNaN(dateObj.getTime())) return '-'
        const dia = String(dateObj.getDate()).padStart(2, '0')
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0')
        const ano = dateObj.getFullYear()
        return `${dia}/${mes}/${ano}`
      } catch (e) {
        return '-'
      }
    },
    getStatusClass(status) {
      return {
        'badge badge-warning': status === 'P',
        'badge badge-success': status === 'D',
        'badge badge-danger': status === 'I'
      }
    },
    getStatusText(status) {
      const statuses = { P: 'Pendente', D: 'Deferido', I: 'Indeferido' }
      return statuses[status] || status
    }
  }
}
</script>

<style lang="scss" scoped>
.recurso-analise {
  .page-header {
    background: white;
    border-radius: 15px;
    padding: 25px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;

    .page-title {
      margin: 0 0 6px 0;
      font-size: 24px;
      font-weight: 600;
      color: #2c3e50;
      display: flex;
      align-items: center;
      gap: 10px;

      i {
        color: #667eea;
      }
    }

    .page-subtitle {
      margin: 0;
      font-size: 14px;
      color: #6c757d;
    }

    .btn {
      border: none;
      border-radius: 8px;
      padding: 10px 16px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      &.btn-secondary {
        background: #6c757d;
        color: white;
      }
    }
  }

  .state {
    background: white;
    border-radius: 15px;
    padding: 60px 30px;
    text-align: center;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    i {
      font-size: 48px;
      color: #667eea;
      margin-bottom: 15px;
      opacity: 0.8;
    }
    p {
      margin: 0;
      color: #6c757d;
    }
  }

  .content {
    display: grid;
    gap: 20px;
  }

  .card {
    background: white;
    border-radius: 15px;
    padding: 25px 30px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .card-title {
      margin: 0 0 15px 0;
      font-size: 16px;
      font-weight: 600;
      color: #495057;
      display: flex;
      align-items: center;
      gap: 8px;

      i {
        color: #667eea;
        font-size: 14px;
      }
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 15px;

      .item {
        &.full {
          grid-column: 1 / -1;
        }
        label {
          display: block;
          font-weight: 600;
          color: #6c757d;
          font-size: 13px;
          margin-bottom: 5px;
        }
        span {
          display: block;
          color: #2c3e50;
          font-size: 14px;
        }
        .pre {
          white-space: pre-wrap;
          background: #f8f9fa;
          border-radius: 8px;
          padding: 12px;
          border: 1px solid #e9ecef;
        }
      }
    }

    .badge {
      display: inline-flex;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      &.badge-warning {
        background: #ffc107;
        color: #212529;
      }
      &.badge-success {
        background: #28a745;
        color: white;
      }
      &.badge-danger {
        background: #dc3545;
        color: white;
      }
    }

    .form {
      .form-group {
        margin-bottom: 15px;
        label {
          display: block;
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
          font-size: 14px;
        }
        .form-control {
          width: 100%;
          padding: 10px 12px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 14px;
          &:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
        }
        textarea.form-control {
          resize: vertical;
          min-height: 110px;
        }
      }
    }

    .actions {
      margin-top: 10px;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;

      .btn {
        padding: 10px 16px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        transition: all 0.2s ease;

        &.btn-secondary {
          background: #6c757d;
          color: white;
        }
        &.btn-warning {
          background: #ffc107;
          color: #212529;
        }
        &.btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
      }
    }
  }
}
</style>
